import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
    hover?: boolean;
}

/**
 * Card component following BottleShop design system
 */
const Card = forwardRef<HTMLDivElement, CardProps>(
    ({ children, hover = false, className = '', ...props }, ref) => {
        const baseStyles =
            'bg-white border border-[#e2e8f0] rounded-xl shadow-sm transition-all duration-300';
        const hoverStyles = hover ? 'hover:shadow-md hover:border-[#cbd5e1]' : '';

        return (
            <div ref={ref} className={`${baseStyles} ${hoverStyles} ${className}`} {...props}>
                {children}
            </div>
        );
    }
);

Card.displayName = 'Card';

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
    ({ children, className = '', ...props }, ref) => {
        return (
            <div ref={ref} className={`px-6 py-4 ${className}`} {...props}>
                {children}
            </div>
        );
    }
);

CardHeader.displayName = 'CardHeader';

export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
    children: ReactNode;
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
    ({ children, className = '', ...props }, ref) => {
        return (
            <h3
                ref={ref}
                className={`text-xl font-semibold text-[#1e293b] ${className}`}
                {...props}
            >
                {children}
            </h3>
        );
    }
);

CardTitle.displayName = 'CardTitle';

export interface CardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {
    children: ReactNode;
}

export const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
    ({ children, className = '', ...props }, ref) => {
        return (
            <p ref={ref} className={`text-sm text-[#64748b] mt-1 ${className}`} {...props}>
                {children}
            </p>
        );
    }
);

CardDescription.displayName = 'CardDescription';

export interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
    ({ children, className = '', ...props }, ref) => {
        return (
            <div ref={ref} className={`px-6 py-4 ${className}`} {...props}>
                {children}
            </div>
        );
    }
);

CardContent.displayName = 'CardContent';

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
    ({ children, className = '', ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={`px-6 py-4 flex items-center justify-end gap-4 ${className}`}
                {...props}
            >
                {children}
            </div>
        );
    }
);

CardFooter.displayName = 'CardFooter';

export default Card;
